import { Stack } from "@mantine/core";
import type {
  PensionPolicy,
  TeaserData,
} from "../../hooks/api/useGetInsurelyFees";
import { AppWebviewLayout } from "./AppWebviewLayout";
import { AskSamSuggestions } from "./AskSamSuggestions";
import { ForecastBanner } from "./ForecastBanner";
import { LoaderView } from "./LoaderView";
import { PensionOverview } from "./PensionOverview";
import {
  expiredPolicyCount,
  type CollectionOutcomes,
} from "../../utils/insurelyCollections";

interface CalculationScreenProps {
  teaser: TeaserData | undefined;
  policies: PensionPolicy[];
  outcomes?: CollectionOutcomes;
  calculatedAt?: string;
  isLoading: boolean;
  /** The advisor request is already in, so the banner confirms instead of asking again. */
  contactSent: boolean;
  onAddMore: () => void;
  onContact: () => void;
  onRefresh: () => void;
  onDelete?: () => Promise<boolean>;
  showAskSam?: boolean;
  /** Data was removed: the empty overview replaces teaser, banner and loader. */
  deleted?: boolean;
}

function LoadingView() {
  return (
    <AppWebviewLayout>
      <LoaderView message="Var god vänta medan vi kontrollerar din pension..." />
    </AppWebviewLayout>
  );
}

export function CalculationScreen({
  teaser,
  policies,
  outcomes,
  calculatedAt,
  isLoading,
  contactSent,
  onAddMore,
  onContact,
  onRefresh,
  onDelete,
  showAskSam,
  deleted,
}: CalculationScreenProps) {
  // Nothing stored means no calculation to wait for.
  if (!deleted && (isLoading || !teaser)) return <LoadingView />;

  // Backend picks the teaser and pre-computes the copy; either string can be null, and no
  // copy means no fee saving to offer an advisor call for.
  const teaserText =
    teaser?.teaserDisplayed === "percentTeaser"
      ? teaser.percentTeaser
      : teaser?.sekTeaser;

  const isExpired = expiredPolicyCount(policies) > 0;

  return (
    <AppWebviewLayout>
      <Stack gap="xl">
        {!isExpired && teaserText && (
          <ForecastBanner
            teaserText={teaserText}
            calculatedAt={calculatedAt}
            onContact={contactSent ? undefined : onContact}
          />
        )}
        <PensionOverview
          policies={policies}
          onAddMore={onAddMore}
          onRefresh={onRefresh}
          onDelete={onDelete}
          outcomes={outcomes}
          deleted={deleted}
        />
        {!teaserText && showAskSam && <AskSamSuggestions />}
      </Stack>
    </AppWebviewLayout>
  );
}
